import {
  LINK_TO_TYPES,
  UNIQUE_CONSTRAINT_VIOLATION_CODE,
  UniqueConstraintViolationReason,
  actionEndEventDataSchema,
  actionStartEventDataSchema,
  actionStepNotificationEventDataSchema,
} from '@doss/common-all'
import { Box, Chip, Stack, Theme, Typography } from '@mui/material'
import _ from 'lodash'
import { PiArrowSquareOut } from 'react-icons/pi'
import { RxCheckCircled, RxCrossCircled, RxLightningBolt, RxPlay } from 'react-icons/rx'
import { EventType } from '~/common/generated/apollo-operations'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import {
  ActivityEventItemType,
  DossActivityTimelineStyles,
} from '~/components/activity/ActivityTimeline/ActivityTimeline'
import { PaginatedNotificationEvent } from '~/components/activity/types'
import Link from '~/components/shared/Link'
import type { ActivityItemProps } from './event'
import { getNotificationIconAndColor } from './notification'

/**
 * Push notifications still ride the legacy Prisma `Event` contract.
 *
 * The activity feed moved to the system_events log, but the three notification types have no
 * SystemEventType counterpart -- a notification is a message to one user, not a record of what
 * happened to an object -- so they keep the payloads `createEvent` writes.
 */

function getNotificationComponents(
  t: (key: TranslationKeys, options?: Record<string, unknown>) => string,
  paginatedEvent: PaginatedNotificationEvent,
  styles: DossActivityTimelineStyles,
  theme: Theme,
): {
  icon: JSX.Element
  details: JSX.Element
  action: string
} {
  const event = paginatedEvent.event
  let icon = <></>
  let details = <></>
  let action = ''

  if (!event.data) {
    return { icon, details, action }
  }

  switch (event.type) {
    case EventType.ACTION_START: {
      if (actionStartEventDataSchema.isValidSync(event.data)) {
        icon = <RxLightningBolt color={theme.palette.common.doss.orange[8]} fontSize="16px" />
        action = `${event.data.actionName} started`
        details = (
          <Chip
            label={
              <Stack
                alignItems="center"
                direction="row"
                display="flex"
                gap={1}
                justifyContent="flex-start"
              >
                <Box alignItems="center" display="flex">
                  <RxPlay color={theme.palette.common.doss.gray[10]} fontSize="16px" />
                </Box>
                <Typography>{t('events.details.workflow_started')}</Typography>
              </Stack>
            }
            sx={styles.events.statusChip}
          />
        )
      }
      break
    }
    case EventType.ACTION_END: {
      if (actionEndEventDataSchema.isValidSync(event.data)) {
        icon = <RxLightningBolt color={theme.palette.common.doss.orange[8]} fontSize="16px" />
        action = `${event.data.actionName}`
        switch (event.data.status) {
          case 'success': {
            details = (
              <Chip
                label={
                  <Stack
                    alignItems="center"
                    direction="row"
                    display="flex"
                    gap={1}
                    justifyContent="flex-start"
                  >
                    <Box alignItems="center" display="flex">
                      <RxCheckCircled color={theme.palette.common.doss.green[10]} fontSize="16px" />
                    </Box>
                    <Typography>{t('events.details.workflow_completed')}</Typography>
                  </Stack>
                }
                sx={{ ...styles.events.statusChip, ...styles.events.statusChip.success }}
              />
            )
            break
          }
          case 'failure': {
            let failureMessage = t('events.details.workflow_failed_error_message')
            if (event.data.errorType === UNIQUE_CONSTRAINT_VIOLATION_CODE) {
              if (event.data.errorReason === UniqueConstraintViolationReason.KEY_TOO_LARGE) {
                failureMessage = t('events.details.workflow_failed_unique_constraint_key_too_large')
              } else if (
                event.data.errorReason === UniqueConstraintViolationReason.DUPLICATE ||
                event.data.errorReason === undefined
              ) {
                // Events written before reasons were introduced represent duplicate violations.
                failureMessage = t('events.details.workflow_failed_unique_constraint')
              }
            }
            details = (
              <Stack alignItems="flex-start" direction="column" display="flex" gap={1}>
                <Chip
                  label={
                    <Stack
                      alignItems="center"
                      direction="row"
                      display="flex"
                      gap={1}
                      justifyContent="flex-start"
                    >
                      <Box alignItems="center" display="flex">
                        <RxCrossCircled color={theme.palette.common.doss.red[10]} fontSize="16px" />
                      </Box>
                      <Typography>{t('events.details.workflow_failed')}</Typography>
                    </Stack>
                  }
                  sx={{ ...styles.events.statusChip, ...styles.events.statusChip.failure }}
                />
                <Typography
                  color={theme.palette.common.doss.gray[9]}
                  sx={styles.events.statusChip.errorMessage}
                  variant="body2"
                >
                  {failureMessage}
                </Typography>
              </Stack>
            )
            break
          }
        }
      }
      break
    }
    case EventType.ACTION_STEP_NOTIFICATION: {
      if (actionStepNotificationEventDataSchema.isValidSync(event.data)) {
        const eventData = actionStepNotificationEventDataSchema.validateSync(event.data)
        // Get the icon and color based on notification type
        const notificationType = eventData.metadata.notificationType
        const { icon: IconComponent, color } = getNotificationIconAndColor(notificationType, theme)
        icon = <IconComponent color={color} fontSize="16px" />
        action = eventData.metadata.title
        const { linkToPath, linkToType } = eventData.metadata
        details = (
          <Stack direction="column" spacing={1}>
            {linkToPath && linkToType && linkToType !== LINK_TO_TYPES.NONE && (
              <Link
                href={`/${linkToPath}`}
                noLinkStyle
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                  color: theme.palette.common.doss.gray[8],
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: theme.palette.common.doss.gray[9],
                    '& > span': {
                      textDecoration: 'underline',
                    },
                  },
                }}
              >
                <PiArrowSquareOut size={16} />
                <Box component="span">Go to {_.capitalize(linkToType)}</Box>
              </Link>
            )}
            <Typography color={theme.palette.common.doss.gray[10]} variant="body2">
              {eventData.metadata.message}
            </Typography>
          </Stack>
        )
      }
      break
    }
    default:
      break
  }

  return { icon, details, action }
}

/** Timeline props for a pushed notification. */
export function getNotificationItemProps(
  t: (key: TranslationKeys, options?: Record<string, unknown>) => string,
  activity: PaginatedNotificationEvent,
  styles: DossActivityTimelineStyles,
  theme: Theme,
): ActivityItemProps {
  const { action, icon, details } = getNotificationComponents(t, activity, styles, theme)
  // A notification popup shows the title and message alone; its breadcrumbs would point nowhere.
  const breadcrumbs =
    activity.event.type === EventType.ACTION_STEP_NOTIFICATION
      ? []
      : [{ name: `${activity.objectName} / Workflows`, link: '' }]

  return {
    type: ActivityEventItemType.WORKFLOW,
    user: activity.event.createdBy?.firstName ?? 'Doss',
    action,
    icon,
    breadcrumbs,
    details,
    time: activity.event.createdAt,
  }
}
